// InfoTab.jsx
import React, { useEffect, useMemo, useState } from "react";
import Classes from "../Information.module.css";
import AddressAutocomplete from "./AddressAutocomplete";
import { setCompanyInfo } from "../../../../../../redux-store/auth/actions";
import { useDispatch, useSelector } from "react-redux";
import backend from "../../../../../../api";

export default function InfoTab() {
  const dispatch = useDispatch();

  const { user } = useSelector((s) => s?.auth || {});
  const userId = user?._id;

  const onBoarding = useSelector((state) => state.companyInfo.info);
  const companyId = onBoarding?._id;

  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    companyName: "",
    contactName: "",
    phone: "",
    websiteURL: "",
    address: "",
    city: "",
    state: "",
    postalCode: "",
    // country: "",
    lat: "",
    lng: "",
    placeId: "",
  });

  useEffect(() => {
    if (!onBoarding) return;
    const a = onBoarding.address || {};
    setForm((prev) => ({
      ...prev,
      companyName: onBoarding.companyName || "",
      contactName: onBoarding.contactName || "",
      phone: onBoarding.phone || "",
      websiteURL: onBoarding.websiteURL || "",
      address: a.line1 || onBoarding.address || "",
      city: a.city || onBoarding.city || "",
      state: a.state || onBoarding.state || "",
      postalCode: a.postalCode || onBoarding.postalCode || "",
      // country: a.country || onBoarding.country || "",
      lat: (a.lat ?? onBoarding.lat ?? "")?.toString() || "",
      lng: (a.lng ?? onBoarding.lng ?? "")?.toString() || "",
      placeId: a.placeId || onBoarding.placeId || "",
    }));
  }, [onBoarding]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddressChange = (text) => {
    setForm((prev) => ({ ...prev, address: text }));
  };

  const handleAddressSelect = (result) => {
    setForm((prev) => ({
      ...prev,
      address: result.formatted || prev.address,
      city: result.components?.city || "",
      state: result.components?.state || "",
      postalCode: result.components?.postalCode || "",
      // country: result.components?.country || "",
      lat: result.lat ?? "",
      lng: result.lng ?? "",
      placeId: result.placeId ?? "",
    }));
  };

  const payload = useMemo(() => {
    const latNum = Number(form.lat);
    const lngNum = Number(form.lng);
    return {
      // backend is lenient; front-end keeps these required
      companyName: form.companyName?.trim() || "",
      contactName: form.contactName?.trim() || "",
      phone: form.phone?.trim() || "",
      websiteURL: form.websiteURL?.trim() || "",
      userId, // required on create
      address: {
        line1: form.address,
        city: form.city,
        state: form.state,
        postalCode: form.postalCode,
        // country: form.country,
        placeId: form.placeId,
        lat: Number.isFinite(latNum) ? latNum : null,
        lng: Number.isFinite(lngNum) ? lngNum : null,
      },
    };
  }, [form, userId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const url = companyId ? `/api/onboarding/${companyId}` : `/api/onboarding`;
      const method = companyId ? "put" : "post";
      const { data } = await backend[method](url, payload);

      if (data?.ok && data?.data) {
        dispatch(setCompanyInfo(data.data));
      } else {
        alert(data?.error || "Unable to save company info.");
      }
    } catch (err) {
      console.error("Save company error:", err);
      alert(err?.response?.data?.error || "Server error. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className={Classes.form} onSubmit={handleSubmit}>
      <div className={Classes.flexColGroup}>
        <div className={Classes.flexCol1}>
          <h5 className={Classes.label}>Company Name</h5>
          <input
            type="text"
            className={Classes.inputField}
            name="companyName"
            value={form.companyName}
            onChange={onChange}
            placeholder="Booster"
            required
          />
        </div>
        <div className={Classes.flexCol1}>
          <h5 className={Classes.label}>Primary Contact Name</h5>
          <input
            type="text"
            className={Classes.inputField}
            name="contactName"
            value={form.contactName}
            onChange={onChange}
            placeholder="John Smith"
            required
          />
        </div>
      </div>

      <div className={Classes.flexColGroup}>
        <div className={Classes.flexCol1}>
          <h5 className={Classes.label}>Phone Number</h5>
          <input
            type="text"
            className={Classes.inputField}
            name="phone"
            value={form.phone}
            onChange={onChange}
            placeholder="(111) 111-1111"
            required
          />
        </div>
        <div className={Classes.flexCol1}>
          <div className={Classes.flexAlign10}>
            <h5 className={Classes.label}>Website URL</h5>
            <div className={Classes.optionalText}><h5>Optional</h5></div>
          </div>
          <input
            type="text"
            className={Classes.inputField}
            name="websiteURL"
            value={form.websiteURL}
            onChange={onChange}
            placeholder="www.booster.com"
          />
        </div>
      </div>

      <div className={Classes.flexColGroup}>
        <div className={Classes.flexCol1}>
          <h5 className={Classes.label}>Address</h5>

          <AddressAutocomplete
            value={form.address}
            onChange={handleAddressChange}
            onSelect={handleAddressSelect}
            // apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY} // optional
            // country="us"
          />

          <div className={Classes.addrMetaRow}>
            <input
              className={Classes.inputField}
              placeholder="City"
              name="city"
              value={form.city}
              onChange={onChange}
            />
            <input
              className={Classes.inputField}
              placeholder="State/Province"
              name="state"
              value={form.state}
              onChange={onChange}
            />
          </div>
          <div className={Classes.addrMetaRow}>
            <input
              className={Classes.inputField}
              placeholder="Postal Code"
              name="postalCode"
              value={form.postalCode}
              onChange={onChange}
            />
            {/* <input
              className={Classes.inputField}
              placeholder="Country"
              name="country"
              value={form.country}
              onChange={onChange}
            /> */}
          </div>
        </div>
      </div>

      <div className={Classes.actions}>
        <button type="submit" className={Classes.saveBtn} disabled={saving}>
          <h5>{saving ? "Saving..." : "Save"}</h5>
        </button>
      </div>
    </form>
  );
}
